const text = (value) => String(value ?? "").trim();
const upper = (value) => text(value).toUpperCase();

export function canManageGuruPlays(profile = {}) {
  const role = upper(profile.role);
  const platformRoles = new Set((profile.platformRoles || []).map((value) => text(value).toLowerCase()));
  const permissions = new Set(profile.permissions || []);
  return role === "ADMIN" || role === "GURU" || platformRoles.has("platform administrator") ||
    permissions.has("guru_plays.edit") || permissions.has("users.edit");
}

export const tegExperienceForProfile = (profile = {}) => canManageGuruPlays(profile) ? "COMMAND_CENTER" : "SIMPLE";

export function tegExperienceForAccess({ accessUser = {}, profile = {} } = {}) {
  return tegExperienceForProfile({ ...profile, role: accessUser.role || profile.role });
}

export function subscriberDirection(row = {}) {
  const signal = upper(row.TEG_Signal ?? row.signal ?? row.Signal);
  if (signal === "BULLISH") return { value: "BULLISH", symbol: "↑" };
  if (signal === "BEARISH") return { value: "BEARISH", symbol: "↓" };
  const entry = upper(row.TEG_Entry_Timing ?? row.Entry_Timing ?? row.entry_timing);
  const option = upper(row.TEG_Option_Strategy ?? row.Option_Strategy ?? row.option_strategy);
  if (entry.includes("WAIT") || entry.includes("NO_TRADE") || option.includes("WAIT") || option.includes("NO_TRADE")) return { value: "NEUTRAL", symbol: "—" };
  if (option.includes("CALL")) return { value: "BULLISH", symbol: "↑" };
  if (option.includes("PUT")) return { value: "BEARISH", symbol: "↓" };
  return { value: "NEUTRAL", symbol: "—" };
}

export function subscriberStatus(row = {}) {
  const entry = upper(row.TEG_Entry_Timing ?? row.Entry_Timing ?? row.entry_timing);
  const action = upper(row.TEG_Action ?? row.action);
  const tradability = upper(row.TEG_Tradability ?? row.TEG_Structure_Action);
  const values = `${entry} ${action} ${tradability}`;
  if (values.includes("NO_TRADE") || values.includes("AVOID")) return "NO PLAY";
  if (values.includes("WAIT") || values.includes("WATCH")) return "WATCHING";
  if (values.includes("EXECUTION READY") || values.includes("TRADEABLE") || entry.includes("PRE_OK") || entry.includes("EARLY")) return "READY";
  return "WATCHING";
}

export function normalizeSimpleOpportunity(row = {}) {
  const direction = subscriberDirection(row);
  return Object.freeze({
    ticker: upper(row.ticker ?? row.Ticker ?? row.symbol),
    company: text(row.company ?? row.company_earnings ?? row.Company),
    direction: direction.value,
    directionSymbol: direction.symbol,
    status: subscriberStatus(row),
    risk: "NOT RATED",
    earningsDay: text(row.day ?? row.Day),
    earningsDate: text(row.earnings_date ?? row.Earnings_Date).slice(0, 10),
    reportTime: text(row.report_time ?? row.raw_report_time ?? row.ReportTime),
  });
}

export function publishedGuruPlaysByTicker(plays = []) {
  return (Array.isArray(plays) ? plays : []).reduce((result, play) => {
    if (play?.publication_state !== "PUBLISHED") return result;
    const ticker = upper(play.ticker);
    if (!ticker) return result;
    const prior = result[ticker];
    const currentTime = Date.parse(play.published_at || play.updated_at || "") || 0;
    const priorTime = Date.parse(prior?.published_at || prior?.updated_at || "") || 0;
    if (!prior || currentTime >= priorTime) result[ticker] = play;
    return result;
  }, {});
}

export function guruPlayDirection(play = {}) {
  const direction = upper(play.direction);
  if (direction === "CALL" || direction === "BULLISH") return { value: "BULLISH", symbol: "↑" };
  if (direction === "PUT" || direction === "BEARISH") return { value: "BEARISH", symbol: "↓" };
  return { value: "NEUTRAL", symbol: "—" };
}

export const PUBLIC_GURU_PLAY_KEYS = Object.freeze([
  "id", "source", "ticker", "direction", "structure", "selected_strike", "expiration",
  "long_strike", "short_strike", "status", "publication_state", "published_at", "updated_at",
  "price_objectives", "guru_risk", "public_risk_flags", "public_note",
]);
